import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  type CampaignListResponse,
  type LeaderboardEntry,
  type LeaderboardResponse,
  type PublicCampaign,
} from '@nexus/shared';

import { ApiError, apiGet } from '../lib/api';
import {
  Badge,
  Card,
  DataTable,
  EmptyState,
  ExternalLink,
  LoadErrorPage,
  LoadingPage,
  Medal,
  Money,
  Num,
  PageHeader,
  SelectField,
  Skeleton,
  Stat,
  StatGrid,
  type Column,
} from '../ui';

/**
 * The per-campaign leaderboard.
 *
 * ## What it is honest about
 *
 * Only verified views count, and a post joins the board only after its window
 * calculation — so the number beside a creator's name is one the fraud rules
 * have already run on. The page says so out loud, because a creator whose
 * yesterday's clip is not on the board yet will otherwise assume it was
 * missed.
 *
 * ## Two columns, not one
 *
 * Campaign-to-date decides the campaign; the last seven days is the one that
 * moves, and a board that only shows the total is a board where whoever
 * started first stays first and everybody else stops opening it.
 *
 * ## Whose earnings are on it
 *
 * Yours, and the top three's. Views are the competition; what somebody was paid
 * is between them and us — except at the top, where "Mara earned $203 here" is
 * the sentence that makes the next creator open the campaign. The API sends
 * `null` for everybody else, so there is nothing here to hide: the column
 * prints what it was sent. The three at the top get a medal, drawn in
 * `ui/Glyph.tsx`, never an emoji.
 */
export function Leaderboard(): React.JSX.Element {
  const [campaigns, setCampaigns] = useState<PublicCampaign[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [board, setBoard] = useState<LeaderboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    void (async () => {
      try {
        const body = await apiGet<CampaignListResponse>('/campaigns');
        /* Only the campaigns this creator has actually joined: the board is for
           the people competing on it, and the API refuses the rest anyway. */
        const joined = body.campaigns.filter((c) => c.participation?.status === 'JOINED');
        setCampaigns(joined);
        setSelected(joined[0]?.slug ?? null);
        setError(null);
      } catch {
        setError('Could not load your campaigns.');
      }
    })();
  }, [attempt]);

  useEffect(() => {
    if (!selected) return;
    setBoard(null);
    void (async () => {
      try {
        setBoard(await apiGet<LeaderboardResponse>(`/campaigns/${selected}/leaderboard`));
        setError(null);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Could not load the leaderboard.');
      }
    })();
  }, [selected, attempt]);

  if (error) {
    return (
      <LoadErrorPage
        eyebrow="Creator"
        title="Leaderboard"
        heading="Could not load the leaderboard."
        onRetry={() => {
          setError(null);
          setBoard(null);
          setAttempt((a) => a + 1);
        }}
      >
        <span>{error} Nobody's position changes because a page did not load.</span>
      </LoadErrorPage>
    );
  }

  if (campaigns === null) return <LoadingPage eyebrow="Creator" title="Leaderboard" />;

  if (campaigns.length === 0) {
    return (
      <>
        <PageHeader eyebrow="Creator" title="Leaderboard" />
        <Card>
          <EmptyState
            title="There is a board for every campaign you have joined."
            action={
              <Link className="btn btn-primary" to="/campaigns">
                Find one to join
              </Link>
            }
          >
            You have not joined one yet, so there is nothing to rank you on.
          </EmptyState>
        </Card>
      </>
    );
  }

  const you = board?.entries.find((e) => e.isYou) ?? board?.you ?? null;
  const campaignName = campaigns.find((c) => c.slug === selected)?.name;

  return (
    <>
      <PageHeader
        eyebrow="Creator"
        title="Leaderboard"
        lead="Verified views only. A clip joins the board after its 72-hour reading has been checked, so a post from this morning is not here yet."
        actions={
          campaigns.length > 1 && (
            <SelectField
              label="Campaign"
              id="leaderboard-campaign"
              value={selected ?? ''}
              onChange={(event) => setSelected(event.target.value)}
            >
              {campaigns.map((campaign) => (
                <option key={campaign.slug} value={campaign.slug}>
                  {campaign.name}
                </option>
              ))}
            </SelectField>
          )
        }
      />

      {you && (
        <StatGrid>
          <Stat label="Your rank" value={<Num value={you.rank} />} emphasis />
          <Stat label="Your verified views" value={<Num value={you.verifiedViews} />} />
          <Stat
            label="Last 7 days"
            value={<Num value={you.verifiedViewsLast7Days} />}
            note="The number that is still moving."
          />
          <Stat label="You have earned" value={<Money cents={you.earningsCents ?? 0} />} />
        </StatGrid>
      )}

      <Card
        title={campaignName ? `${campaignName} — the board` : 'The board'}
        sub={
          board
            ? `${board.creators} creator${board.creators === 1 ? '' : 's'} with a finished clip.`
            : undefined
        }
      >
        {board === null ? (
          <>
            <p className="sr-only" role="status">
              Loading…
            </p>
            <Skeleton lines={5} />
          </>
        ) : (
          <Board board={board} />
        )}
      </Card>
    </>
  );
}

function Board({ board }: { board: LeaderboardResponse }): React.JSX.Element {
  const rows: LeaderboardEntry[] = board.you ? [...board.entries, board.you] : [...board.entries];

  const columns: Array<Column<LeaderboardEntry>> = [
    {
      key: 'creator',
      header: 'Creator',
      primary: true,
      cell: (entry) => (
        <>
          {entry.rank <= 3 ? (
            <>
              <Medal rank={entry.rank} />{' '}
            </>
          ) : null}
          <span className="num">#{entry.rank}</span> {entry.displayName ?? 'A creator'}
          {entry.isYou && (
            <>
              {' '}
              <Badge tone="positive">you</Badge>
            </>
          )}
        </>
      ),
    },
    {
      key: 'views',
      header: 'Verified views',
      numeric: true,
      align: 'end',
      cell: (entry) => <Num value={entry.verifiedViews} />,
    },
    {
      key: 'last7',
      header: 'Last 7 days',
      numeric: true,
      align: 'end',
      cell: (entry) => <Num value={entry.verifiedViewsLast7Days} />,
    },
    {
      key: 'posts',
      header: 'Clips',
      numeric: true,
      align: 'end',
      cell: (entry) => <Num value={entry.posts} />,
    },
    {
      key: 'best',
      header: 'Best clip',
      align: 'end',
      cell: (entry) =>
        entry.bestPost ? (
          <ExternalLink href={entry.bestPost.postUrl}>
            <Num value={entry.bestPost.verifiedViews} suffix="views" />
          </ExternalLink>
        ) : (
          <span className="muted">—</span>
        ),
    },
    {
      key: 'earned',
      header: 'Earned',
      numeric: true,
      align: 'end',
      /* What the API sent: your own, and the medals'. Null is a dash. */
      cell: (entry) =>
        entry.earningsCents === null ? (
          <span className="muted">—</span>
        ) : (
          <Money cents={entry.earningsCents} />
        ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={rows}
      getKey={(entry) => `${entry.creatorId}-${entry.rank}`}
      isHighlighted={(entry) => entry.isYou}
      empty={
        <EmptyState title="Nobody has a finished clip on this campaign yet.">
          Yours could be the first. A clip appears here about three days after you post it.
        </EmptyState>
      }
    />
  );
}
